import React, { useState, useEffect } from 'react';
import { creditMemoApi, clientApi, invoiceApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Plus, Download, Send, Trash2, ReceiptText } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

export default function CreditMemos() {
  const [memos, setMemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ client_id: '', invoice_id: '', amount: '', reason: '', notes: '' });

  useEffect(() => { fetchMemos(); }, []);

  const fetchMemos = async () => {
    try {
      const res = await creditMemoApi.getAll();
      setMemos(res.data || []);
    } catch (e) {
      toast.error('Failed to load credit memos');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = async () => {
    setForm({ client_id: '', invoice_id: '', amount: '', reason: '', notes: '' });
    setInvoices([]);
    setOpen(true);
    try {
      const res = await clientApi.getAll();
      setClients(res.data || []);
    } catch (e) { /* ignore */ }
  };

  const onClientChange = async (clientId) => {
    setForm((f) => ({ ...f, client_id: clientId, invoice_id: '' }));
    try {
      const res = await invoiceApi.getAll({ client_id: clientId });
      setInvoices(res.data || []);
    } catch (e) { setInvoices([]); }
  };

  const handleCreate = async () => {
    if (!form.client_id) { toast.error('Select a client'); return; }
    if (!form.reason.trim()) { toast.error('Enter a reason'); return; }
    const amount = parseFloat(form.amount);
    if (!(amount > 0)) { toast.error('Enter an amount greater than 0'); return; }
    setSaving(true);
    try {
      const res = await creditMemoApi.create({
        client_id: form.client_id,
        invoice_id: form.invoice_id || null,
        amount,
        reason: form.reason.trim(),
        notes: form.notes.trim() || null,
      });
      toast.success(`Credit memo ${res.data.credit_memo_number} created`);
      setOpen(false);
      fetchMemos();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create credit memo');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (memo) => {
    try {
      const res = await creditMemoApi.getPdf(memo.id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) { toast.error('Failed to open PDF'); }
  };

  const handleSend = async (memo) => {
    try {
      const res = await creditMemoApi.send(memo.id);
      toast.success(res.data.message || 'Credit memo sent');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send'); }
  };

  const handleDelete = async (memo) => {
    if (!window.confirm(`Delete credit memo ${memo.credit_memo_number}?`)) return;
    try {
      await creditMemoApi.delete(memo.id);
      toast.success('Credit memo deleted');
      fetchMemos();
    } catch (e) { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6" data-testid="credit-memos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Credit Memos</h1>
          <p className="text-muted-foreground">Documents recording a credit or refund owed to a client</p>
        </div>
        <Button onClick={openDialog} data-testid="new-credit-memo-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Credit Memo
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ReceiptText className="h-4 w-4 text-muted-foreground" />
            All Credit Memos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground py-6 text-center">Loading…</p>
          ) : memos.length === 0 ? (
            <p className="text-muted-foreground py-6 text-center" data-testid="no-credit-memos">
              No credit memos yet. Create one when you owe a client money back.
            </p>
          ) : (
            <div className="divide-y">
              {memos.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 py-3 flex-wrap" data-testid={`credit-memo-${m.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium">{m.credit_memo_number}
                      {m.invoice_number && <span className="text-muted-foreground font-normal"> · re: {m.invoice_number}</span>}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">{m.client_name} — {m.reason}</p>
                    <p className="text-xs text-muted-foreground">{m.date}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-blue-700 tabular-nums">-{fmt(m.amount)}</span>
                    <Button variant="ghost" size="sm" onClick={() => handleDownload(m)} data-testid={`cm-pdf-${m.id}`}>
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleSend(m)} data-testid={`cm-send-${m.id}`}>
                      <Send className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(m)} data-testid={`cm-delete-${m.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Credit Memo</DialogTitle>
            <DialogDescription>Record a credit or refund owed to a client. This does not track a running balance.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Client *</Label>
              <Select value={form.client_id} onValueChange={onClientChange}>
                <SelectTrigger data-testid="cm-client-select"><SelectValue placeholder="Select a client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name || `${c.first_name} ${c.last_name}`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.client_id && invoices.length > 0 && (
              <div>
                <Label>Related Invoice (optional)</Label>
                <Select value={form.invoice_id || 'none'} onValueChange={(v) => setForm((f) => ({ ...f, invoice_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="cm-invoice-select"><SelectValue placeholder="Link an invoice" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No linked invoice</SelectItem>
                    {invoices.map((inv) => (
                      <SelectItem key={inv.id} value={inv.id}>{inv.invoice_number} — {fmt(inv.total)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Credit Amount *</Label>
              <Input type="number" step="0.01" min="0.01" value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder="e.g. 100.00" data-testid="cm-amount-input" />
            </div>
            <div>
              <Label>Reason *</Label>
              <Input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Overpayment refund / work not performed" data-testid="cm-reason-input" />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} data-testid="cm-create-btn">Create Credit Memo</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
